import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from 'recharts';
import { ScanLine, ChevronRight, Trophy, Layers, Loader2, AlertCircle, Flame, ArrowUpRight, GitCompareArrows } from 'lucide-react';
import { motion } from 'framer-motion';
import { api, formatDate, scoreBg, videoThumbUrl } from '../lib/api';
import { useLanguage } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { ScoreRing } from '../components/ScoreRing';
import { Skeleton } from '../components/ui/skeleton';

const ChartTooltip = ({ active, payload, lang }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-[#1E1E1E] border border-white/10 rounded-[14px] px-3 py-2 text-xs">
      <div className="font-mono text-white font-medium">{d.score}/100</div>
      <div className="text-white/55">{lang === 'it' ? d.move_label_it : d.move_label_en}</div>
    </div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { t, lang, moveLabel } = useLanguage();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, l] = await Promise.all([api.get('/dashboard/stats'), api.get('/analyses?limit=5')]);
        setStats(s.data);
        setRecent(l.data.items);
      } catch {
        setError(true);
      }
    })();
  }, []);

  const chartData = (stats?.trend || []).map((p, i) => ({ ...p, idx: i + 1 }));
  const highlights = stats?.community_highlights || [];

  return (
    <div className="space-y-4" data-testid="dashboard-page">
      <div className="pt-2 flex items-end justify-between">
        <div>
          <p className="font-mono text-xs text-white/40 uppercase tracking-widest">
            {new Date().toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
          <h1 className="font-display font-bold text-2xl tracking-tight mt-0.5 text-white" data-testid="dashboard-greeting">
            {user?.name?.split(' ')[0] || 'Athlete'}
          </h1>
        </div>
      </div>

      {error && (
        <div className="ss-card p-4 flex items-center gap-3 text-sm text-[#E76F7B]" data-testid="dashboard-error">
          <AlertCircle size={16} /> Failed to load data.
        </div>
      )}

      {/* ---- BENTO GRID ---- */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3 md:gap-4">
        {/* Skill Rating tile */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="ss-card p-5 md:col-span-3" data-testid="dashboard-skill-rating-tile">
          <div className="flex items-center justify-between">
            <div className="space-y-3">
              <div>
                <h2 className="font-display font-bold text-lg tracking-tight text-white">{t('skillRating')}</h2>
                <p className="font-mono text-[11px] text-white/40">{t('avgOfAll')}</p>
              </div>
              <div className="flex gap-5">
                <div>
                  <div className="flex items-center gap-1.5 text-white/40">
                    <Layers size={12} />
                    <span className="font-mono text-[10px] uppercase">{t('totalAnalyses')}</span>
                  </div>
                  <div className="font-display font-bold text-xl tabular-nums text-white" data-testid="dashboard-total-analyses">
                    {stats ? stats.total_analyses : '\u2014'}
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-1.5 text-white/40">
                    <Trophy size={12} />
                    <span className="font-mono text-[10px] uppercase">{t('bestScore')}</span>
                  </div>
                  <div className="font-display font-bold text-xl tabular-nums text-white" data-testid="dashboard-best-score">
                    {stats?.best_score ?? '\u2014'}
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="ss-halo absolute inset-0 pointer-events-none rounded-full" style={{ background: 'radial-gradient(90px circle at 50% 50%, rgba(255,255,255,0.08), transparent 70%)' }} />
              {stats ? (
                <ScoreRing score={stats.skill_rating} size={124} testId="dashboard-skill-rating" />
              ) : (
                <Skeleton className="w-31 h-31 rounded-full bg-white/[0.05]" style={{ width: 124, height: 124 }} />
              )}
            </div>
          </div>
        </motion.div>

        {/* Trend chart tile */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }} className="ss-card p-5 md:col-span-3" data-testid="dashboard-trend-chart-tile">
          <h2 className="font-display font-bold text-lg tracking-tight text-white mb-3">{t('progressTitle')}</h2>
          {!stats ? (
            <Skeleton className="h-32 w-full bg-white/[0.05] rounded-[14px]" />
          ) : chartData.length >= 2 ? (
            <div className="h-36" data-testid="dashboard-progress-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -22 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="idx" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10, fontFamily: 'IBM Plex Mono' }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10, fontFamily: 'IBM Plex Mono' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip lang={lang} />} cursor={{ stroke: 'rgba(255,255,255,0.2)' }} />
                  <Line type="monotone" dataKey="score" stroke="#22C55E" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: '#22C55E', stroke: 'rgba(34,197,94,0.3)', strokeWidth: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-32 flex flex-col items-center justify-center text-center gap-1" data-testid="dashboard-chart-empty">
              {chartData.length === 1 ? (
                <p className="text-xs text-white/45 font-mono px-6">{t('trendNeedMore')}</p>
              ) : (
                <>
                  <p className="text-sm text-white/55">{t('noAnalysesTitle')}</p>
                  <p className="text-xs text-white/35 font-mono">{t('noAnalysesSub')}</p>
                </>
              )}
            </div>
          )}
        </motion.div>

        {/* Quick actions tile */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="ss-card p-5 md:col-span-2 flex flex-col gap-2.5" data-testid="dashboard-quick-actions-tile">
          <button
            onClick={() => navigate('/analyze')}
            data-testid="dashboard-analyze-cta"
            className="ss-btn-primary w-full h-12 font-display font-bold text-sm flex items-center justify-center gap-2"
          >
            <ScanLine size={17} />
            {t('analyzeNewSkill')}
          </button>
          <button
            onClick={() => navigate('/pitch')}
            data-testid="dashboard-pitch-cta"
            className="ss-btn-glass w-full h-11 text-sm font-medium flex items-center justify-center gap-2"
          >
            <Flame size={15} className="text-white/70" /> {t('navPitch')}
          </button>
          <button
            onClick={() => navigate('/pitch/compare')}
            data-testid="dashboard-compare-cta"
            className="ss-btn-glass w-full h-11 text-sm font-medium flex items-center justify-center gap-2"
          >
            <GitCompareArrows size={15} className="text-[#22C55E]" /> {t('compare')}
          </button>
        </motion.div>

        {/* Recent analyses tile */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }} className="ss-card p-4 md:col-span-4" data-testid="dashboard-recent-analyses-tile">
          <div className="flex items-center justify-between px-1 mb-2">
            <h2 className="font-display font-bold text-lg tracking-tight text-white">{t('recentAnalyses')}</h2>
            {recent?.length > 0 && (
              <button onClick={() => navigate('/library')} data-testid="dashboard-view-all" className="text-xs font-mono text-white/60 hover:text-white transition-colors flex items-center gap-0.5">
                {t('viewAll')} <ChevronRight size={13} />
              </button>
            )}
          </div>

          {!recent ? (
            <div className="space-y-2">
              {[0, 1].map((i) => (
                <Skeleton key={i} className="h-[64px] w-full bg-white/[0.05] rounded-[14px]" />
              ))}
            </div>
          ) : recent.length === 0 ? (
            <div className="py-6 text-center" data-testid="dashboard-recent-empty">
              <p className="text-sm text-white/55">{t('noAnalysesTitle')}</p>
              <p className="text-xs text-white/35 font-mono mt-1">{t('noAnalysesSub')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recent.slice(0, 4).map((item) => (
                <button
                  key={item.analysis_id}
                  onClick={() => navigate(`/result/${item.analysis_id}`)}
                  data-testid={`dashboard-recent-item-${item.analysis_id}`}
                  className="w-full p-2.5 rounded-[14px] border border-white/[0.07] bg-white/[0.03] flex items-center gap-3 text-left hover:border-white/25 transition-colors"
                >
                  <div className="w-14 h-10 rounded-[12px] overflow-hidden bg-black/60 border border-white/10 shrink-0 flex items-center justify-center">
                    {item.has_thumbnail ? (
                      <img src={videoThumbUrl(item.analysis_id)} alt="" className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <ScanLine size={14} className="text-white/30" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate text-white/90">
                      {item.status === 'done' ? moveLabel(item.move_type, item) : item.status === 'failed' ? t('failed') : t('processing')}
                    </div>
                    <div className="font-mono text-[11px] text-white/40">{formatDate(item.created_at, lang)}</div>
                  </div>
                  {item.status === 'analyzing' ? (
                    <Loader2 size={16} className="animate-spin text-white/50" />
                  ) : item.overall_score != null ? (
                    <span className={`border rounded-[12px] px-2 py-0.5 font-mono text-xs font-medium tabular-nums ${scoreBg(item.overall_score)}`}>
                      {item.overall_score}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          )}
        </motion.div>

        {/* Community highlights tile */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }} className="ss-card p-4 md:col-span-6" data-testid="dashboard-community-highlights-tile">
          <div className="flex items-center justify-between px-1 mb-2">
            <h2 className="font-display font-bold text-lg tracking-tight text-white flex items-center gap-2">
              <Flame size={16} className="text-white/70" /> {t('communityHighlights')}
            </h2>
            <button onClick={() => navigate('/pitch')} data-testid="dashboard-highlights-view-all" className="text-xs font-mono text-white/60 hover:text-white transition-colors flex items-center gap-0.5">
              {t('viewAll')} <ChevronRight size={13} />
            </button>
          </div>
          {highlights.length === 0 ? (
            <p className="text-xs text-white/40 font-mono px-1 py-3">{t('pitchEmpty')}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              {highlights.map((h) => (
                <button
                  key={h.post_id}
                  onClick={() => navigate('/pitch')}
                  data-testid={`dashboard-highlight-${h.post_id}`}
                  className="p-3 rounded-[14px] border border-white/[0.07] bg-white/[0.03] flex items-center gap-3 text-left hover:border-white/25 transition-colors"
                >
                  <div className="w-12 h-9 rounded-[12px] overflow-hidden bg-black/60 border border-white/10 shrink-0">
                    <img src={videoThumbUrl(h.analysis_id)} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate text-white/85">{lang === 'it' ? h.move_label_it : h.move_label_en}</div>
                    <div className="font-mono text-[10px] text-white/40 truncate">{h.author?.name} · <ArrowUpRight size={9} className="inline" /> {h.upvotes_count}</div>
                  </div>
                  {h.overall_score != null && (
                    <span className={`border rounded-[12px] px-1.5 py-0.5 font-mono text-[11px] tabular-nums ${scoreBg(h.overall_score)}`}>{h.overall_score}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
